/// <amd-module name="Wiki/Search/SearchProvider" />
import React = require("react");
import { ITfsPageService } from "Tfs/Platform/Page";
import { ITfsSearchQuery } from "Tfs/Platform/Search";
import { IInvokedProvidersManager } from "InstantSearch/Shared/Services/InvokedProvidersManager";
import { getQueryTextWithWildCardChar } from "InstantSearch/Shared/Utils";
import { TelemetryWriter } from "InstantSearch/Shared/TelemetryWriter";
import { ObservableValue } from "VSS/Core/Observable";
import { Services, VssService } from "VSS/Platform/Context";
import { isFeatureFlagEnabled, isFeatureEnabled } from "VSS/Platform/Feature";
import { ISearchProvider, ISearchQuery, ISearchResult, ISearchStatus, ISearchInput } from "VSS/Platform/Search";
import { WikiSearchResultComponent } from "./SearchResult";
import { InstantSearchSuggestionsComponent } from "InstantSearch/Shared/SearchSuggestions";

const InfiniteScrollFeatureFlag: string = "WebAccess.Search.Wiki.InfiniteScroll";

const Featurename: string = "WikiSearchSuggestions";

interface IWikiSearchQuery extends ITfsSearchQuery {
    filters: {
        ProjectFilters?: string[];
    };
}

class WikiSearchProvider extends VssService implements ISearchProvider {
    private _wikiSearchSuggestionsComponentRef = React.createRef<InstantSearchSuggestionsComponent>();
    private _telemetryWriter: TelemetryWriter;


    public execute(searchQuery: ISearchQuery): ISearchStatus {
        return { searchQuery: searchQuery, searchResult: new ObservableValue<ISearchResult | undefined>(undefined) };
    }

    public getQuery(searchQuery: ISearchQuery): IWikiSearchQuery {
        const wikiSearchQuery: IWikiSearchQuery = Object.assign({
            type: "wiki",
            filters: {}
        }, searchQuery);

        // Determine which projects we should be searching.
        const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();

        if (tfsPageData && tfsPageData.project) {
            wikiSearchQuery.filters.ProjectFilters = [tfsPageData.project.name];
        }

        if (isFeatureFlagEnabled(this.pageContext, InfiniteScrollFeatureFlag, false)) {
            wikiSearchQuery.pageSize = 25;
        }

        return wikiSearchQuery;
    }

    public help(searchInput: ISearchInput): JSX.Element | null {
        if (isFeatureFlagEnabled(this.pageContext, "Wiki.InstantSearch", false) &&
            isFeatureEnabled(this.pageContext, "ms.vss-tfs-web.enable-search-suggestions", false)) {
            return (<InstantSearchSuggestionsComponent
                ref={this._wikiSearchSuggestionsComponentRef}
                hubName={"Wiki"}
                featureName={Featurename}
                searchInput={searchInput} />
            );
        }

        return null;
    }

    public performAction(searchQuery: ISearchQuery): Promise<boolean> {
        if (isFeatureFlagEnabled(this.pageContext, "Wiki.InstantSearch", false) &&
            isFeatureEnabled(this.pageContext, "ms.vss-tfs-web.enable-search-suggestions", false)) {
            const invokedProvidersManager = this.pageContext.getService<IInvokedProvidersManager>("IInvokedProvidersManager");
            if (!invokedProvidersManager.shouldAllowImmersiveSearch()) {
                // Block all quick navigation scenarios except those 
                // which are supported by search portal.
                return Promise.resolve(true);
            }

            const wikiSuggestionComponent = this._wikiSearchSuggestionsComponentRef.current;
            if (wikiSuggestionComponent && !searchQuery.openInNewWindow) {
                wikiSuggestionComponent.invokeActionOfSelectedItem();
                return Promise.resolve(true);
            } else {
                // Execute search query with wild card char and land on search portal
                searchQuery.queryText = getQueryTextWithWildCardChar(searchQuery.queryText);
                this.telemetryWriter.publish("WikiImmersiveSearchPerformed");
            }
        }

        return Promise.resolve(false);
    }

    public render(searchStatus: ISearchStatus): JSX.Element {
        return <WikiSearchResultComponent searchStatus={searchStatus} />;
    }

    private get telemetryWriter(): TelemetryWriter {
        if (!this._telemetryWriter) {
            this._telemetryWriter = new TelemetryWriter(this.pageContext, Featurename);
        }

        return this._telemetryWriter;
    }
}

Services.add("wiki-search-provider", { serviceFactory: WikiSearchProvider });
